import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { KeycloakEventType, KeycloakService } from '@vectronic/keycloak-angular';
import { adminUserApi, adminUserDtos, adminUserEnums } from '@vectronic/openApi-admin';
import { filter, switchMap } from 'rxjs';

interface LoggedInUserState {
  user: adminUserDtos.User;
  loaded: boolean;
}
@Injectable({ providedIn: 'root' })
export class LoggedInUserService {
  private readonly userRestService = inject(adminUserApi.Users);
  private readonly authService = inject(KeycloakService);

  //state
  private state = signal<LoggedInUserState>({ user: undefined, loaded: false });

  // selectors
  user = computed(() => this.state().user);
  isLoaded = computed(() => this.state().loaded);
  isLoaded$ = toObservable(this.isLoaded);
  accessList = computed(() => this.user()?.accessList ?? []);
  accessScopes = computed(() => this.accessList().map(({ scope }) => scope));

  // sources
  private loggedInUserInfoLoaded$ = this.userRestService.getSelf();
  private refreshToken$ = this.authService.keycloakEvents$.pipe(
    takeUntilDestroyed(),
    filter((event) => event.type === KeycloakEventType.OnTokenExpired),
    switchMap(() => this.authService.updateToken()),
  );
  constructor() {
    // reducers
    this.loggedInUserInfoLoaded$
      .pipe(takeUntilDestroyed())
      .subscribe((userInfo) =>
        this.state.update((state) => ({ ...state, loaded: true, user: userInfo })),
      );
    this.refreshToken$.subscribe();
  }

  hasAccess(scope: adminUserEnums.Scope | adminUserEnums.Scope[]) {
    const scopes = Array.isArray(scope) ? scope : [scope];
    return computed(() => scopes.some((targetScope) => this.accessScopes().includes(targetScope)));
  }
}
